import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Fab, Paper, Typography, TextField, IconButton,
    Avatar, Chip, Divider, CircularProgress, Tooltip
} from '@mui/material';
import {
    Chat as ChatIcon,
    Close as CloseIcon,
    Send as SendIcon,
    Delete as DeleteIcon,
    SmartToy as BotIcon,
    Person as PersonIcon,
    AutoAwesome as SparkleIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { sendMessage, fetchChatHistory, clearChat, addMessageLocally } from '../store/slices/chatSlice';
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext';

// ─────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────
const T = {
    bg:           '#08090d',
    surface:      '#0f1117',
    surfaceHigh:  '#161b27',
    border:       'rgba(255,255,255,0.07)',
    borderHover:  'rgba(99,179,237,0.35)',
    accent:       '#63b3ed',
    accentDeep:   '#2b6cb0',
    accentGlow:   'rgba(99,179,237,0.18)',
    accentGlow2:  'rgba(99,179,237,0.08)',
    userBubble:   'linear-gradient(135deg,#1a4a7a 0%,#1e3a5f 100%)',
    botBubble:    '#131924',
    text:         '#e8edf5',
    textMuted:    '#5a6a7e',
    textSub:      '#8899aa',
    green:        '#48bb78',
    radius:       '20px',
};

// ─────────────────────────────────────────────────────────────────
// GLOBAL STYLES (injected once)
// ─────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes chatOpen {
    from { opacity: 0; transform: translateY(24px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0)   scale(1);    }
  }
  @keyframes pulse {
    0%,100% { transform: scale(1);    opacity: 0.7; }
    50%      { transform: scale(1.15); opacity: 1;   }
  }
  @keyframes typingDot {
    0%,80%,100% { transform: translateY(0);   opacity: 0.35; }
    40%         { transform: translateY(-5px); opacity: 1;    }
  }
  @keyframes fabPop {
    0%   { transform: scale(0.8) rotate(-10deg); opacity: 0; }
    60%  { transform: scale(1.08) rotate(3deg);  opacity: 1; }
    100% { transform: scale(1) rotate(0);        opacity: 1; }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  .chat-msg { animation: fadeSlideUp 0.28s cubic-bezier(.22,1,.36,1) both; }
  .chat-window { animation: chatOpen 0.32s cubic-bezier(.22,1,.36,1) both; }
  .fab-btn { animation: fabPop 0.4s cubic-bezier(.22,1,.36,1) both; }
  .scroll-area::-webkit-scrollbar { width: 3px; }
  .scroll-area::-webkit-scrollbar-track { background: transparent; }
  .scroll-area::-webkit-scrollbar-thumb { background: rgba(99,179,237,0.2); border-radius: 4px; }
  .scroll-area::-webkit-scrollbar-thumb:hover { background: rgba(99,179,237,0.4); }
`;

function useGlobalStyle(css) {
    useEffect(() => {
        const id = 'chat-widget-styles';
        if (!document.getElementById(id)) {
            const el = document.createElement('style');
            el.id = id;
            el.textContent = css;
            document.head.appendChild(el);
        }
    }, []);
}

// ─────────────────────────────────────────────────────────────────
// FORMATTED MESSAGE
// ─────────────────────────────────────────────────────────────────
function FormattedMessage({ content }) {
    const lines = content.split('\n');
    return (
        <Box sx={{
            fontSize: '0.8rem',
            lineHeight: 1.75,
            fontFamily: "'DM Sans', sans-serif",
            color: T.text,
        }}>
            {lines.map((line, i) => {
                if (line.startsWith('─') || line.startsWith('━')) {
                    return (
                        <Box key={i} sx={{
                            my: 0.8,
                            height: '1px',
                            background: `linear-gradient(90deg, transparent, ${T.border}, transparent)`
                        }} />
                    );
                }
                if (line.match(/^[•\-\*]\s/)) {
                    return (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pl: 0.5, mb: 0.25 }}>
                            <Box sx={{
                                mt: '6px', width: 4, height: 4, borderRadius: '50%',
                                bgcolor: T.accent, flexShrink: 0
                            }} />
                            <span style={{ color: T.text }}>{line.replace(/^[•\-\*]\s*/, '')}</span>
                        </Box>
                    );
                }
                if (line.match(/^\*\*.+\*\*/) || line.match(/^#{1,3}\s/)) {
                    const clean = line.replace(/^#{1,3}\s/, '').replace(/\*\*/g, '');
                    return (
                        <Box key={i} sx={{
                            fontWeight: 600,
                            color: T.accent,
                            fontSize: '0.78rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            mt: 1, mb: 0.25
                        }}>{clean}</Box>
                    );
                }
                if (line.includes(':') && !line.startsWith(' ') && line.split(':')[0].length < 24) {
                    const colonIdx = line.indexOf(':');
                    const key = line.substring(0, colonIdx);
                    const val = line.substring(colonIdx + 1);
                    return (
                        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.15 }}>
                            <span style={{ color: T.textSub, fontWeight: 500, minWidth: 90, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem' }}>{key}:</span>
                            <span style={{ color: T.text }}>{val}</span>
                        </Box>
                    );
                }
                if (line.trim() === '') return <Box key={i} sx={{ height: 6 }} />;
                return <Box key={i} sx={{ color: T.text }}>{line}</Box>;
            })}
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// QUICK QUERIES
// ─────────────────────────────────────────────────────────────────
const QUICK_QUERIES = [
    { label: 'Plot Inventory',  icon: '◈', query: 'plot inventory bataoo' },
    { label: 'Overdue Plots',   icon: '⚑', query: 'overdue plots sary bataoo with customer names' },
    { label: 'Financial',       icon: '◎', query: 'financial summary bataoo' },
    { label: "Today's Leads",   icon: '◉', query: 'aaj ke leads bataoo' },
    { label: 'Follow-ups',      icon: '◷', query: 'pending followups bataoo' },
    { label: 'Bookings',        icon: '◆', query: 'bookings summary bataoo' },
];

// ─────────────────────────────────────────────────────────────────
// TYPING INDICATOR
// ─────────────────────────────────────────────────────────────────
function TypingIndicator() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
            <BotAvatar />
            <Box sx={{
                display: 'flex', gap: 0.5, alignItems: 'center',
                bgcolor: T.botBubble,
                border: `1px solid ${T.border}`,
                px: 1.8, py: 1.2,
                borderRadius: '16px 16px 16px 4px',
            }}>
                {[0, 0.15, 0.3].map((delay, i) => (
                    <Box key={i} sx={{
                        width: 5, height: 5, borderRadius: '50%',
                        bgcolor: T.accent,
                        animation: `typingDot 1.4s ${delay}s infinite ease-in-out`,
                    }} />
                ))}
            </Box>
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// AVATARS
// ─────────────────────────────────────────────────────────────────
function BotAvatar({ size = 28 }) {
    return (
        <Box sx={{
            width: size, height: size, borderRadius: '10px',
            background: `linear-gradient(135deg, #1a3a5c 0%, #0d2137 100%)`,
            border: `1px solid ${T.borderHover}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 0 12px ${T.accentGlow}`,
        }}>
            <SparkleIcon sx={{ fontSize: size * 0.52, color: T.accent }} />
        </Box>
    );
}

function UserAvatar({ size = 28 }) {
    return (
        <Box sx={{
            width: size, height: size, borderRadius: '10px',
            background: 'linear-gradient(135deg, #1a4a7a 0%, #0f2d4a 100%)',
            border: `1px solid rgba(99,179,237,0.2)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>
            <PersonIcon sx={{ fontSize: size * 0.52, color: '#90caf9' }} />
        </Box>
    );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function ChatWidget() {
    useGlobalStyle(GLOBAL_CSS);

    const dispatch   = useDispatch();
    const chatState  = useSelector(state => state.chat);
    const { activeUser } = useSelector(state => state.auth);
    const loading    = chatState?.loading || false;
    const { activeSociety } = useSidebarContext();

    const [open, setOpen]   = useState(false);
    const [input, setInput] = useState('');
    const [sessionId]       = useState(() => `session_${Date.now()}_${Math.random().toString(36).slice(2)}`);

    const messagesEndRef = useRef(null);
    const inputRef       = useRef(null);

    useEffect(() => {
        if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatState?.messages, open]);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 120);
    }, [open]);

    useEffect(() => {
        if (open && sessionId) dispatch(fetchChatHistory(sessionId));
    }, [open]);

    const handleSend = async (text) => {
        const msg = (text || input).trim();
        if (!msg || loading) return;
        setInput('');
        dispatch(addMessageLocally({ role: 'user', content: msg, createdAt: new Date().toISOString() }));
        dispatch(sendMessage({
            message: msg, sessionId,
            userId: activeUser?._id || null,
            activeSocietyId: activeSociety?._id || null
        }));
    };

    const handleClear = () => {
        if (window.confirm('Clear chat history?')) dispatch(clearChat(sessionId));
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const isStaff = ['Admin', 'Employee'].includes(activeUser?.role);
    const visibleQueries = QUICK_QUERIES.filter(q => isStaff || q.label === 'Plot Inventory');

    return (
        <>
            {/* ── FAB ── */}
            <Box className="fab-btn" sx={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9999 }}>
                <Tooltip title={open ? '' : 'CRM Assistant'} placement="left">
                    <Box
                        onClick={() => setOpen(o => !o)}
                        sx={{
                            width: 54, height: 54,
                            borderRadius: '16px',
                            background: open
                                ? `rgba(20,28,42,0.95)`
                                : `linear-gradient(135deg, #1a5a9c 0%, #0d3a6e 100%)`,
                            border: `1px solid ${open ? T.border : 'rgba(99,179,237,0.4)'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: open
                                ? '0 4px 20px rgba(0,0,0,0.5)'
                                : `0 4px 24px rgba(99,179,237,0.28), 0 0 0 1px rgba(99,179,237,0.15)`,
                            transition: 'all 0.25s cubic-bezier(.22,1,.36,1)',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 32px rgba(99,179,237,0.35)`,
                            },
                        }}
                    >
                        {open
                            ? <CloseIcon sx={{ color: T.textSub, fontSize: 20 }} />
                            : <SparkleIcon sx={{ color: '#fff', fontSize: 22 }} />
                        }
                    </Box>
                </Tooltip>

                {/* Online dot */}
                {!open && (
                    <Box sx={{
                        position: 'absolute', top: -3, right: -3,
                        width: 10, height: 10, borderRadius: '50%',
                        bgcolor: T.green,
                        border: `2px solid ${T.bg}`,
                        animation: 'pulse 2.4s infinite',
                    }} />
                )}
            </Box>

            {/* ── CHAT WINDOW ── */}
            {open && (
                <Box
                    className="chat-window"
                    sx={{
                        position: 'fixed',
                        bottom: 98, right: 28,
                        zIndex: 9999,
                        width: { xs: 'calc(100vw - 32px)', sm: 420 },
                        height: { xs: 'calc(100vh - 140px)', sm: 580 },
                        maxHeight: 620,
                        display: 'flex', flexDirection: 'column',
                        borderRadius: T.radius,
                        bgcolor: T.bg,
                        border: `1px solid ${T.border}`,
                        overflow: 'hidden',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
                        fontFamily: "'DM Sans', sans-serif",
                        // Subtle gradient overlay
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: '180px',
                            background: `radial-gradient(ellipse at 70% 0%, rgba(99,179,237,0.06) 0%, transparent 70%)`,
                            pointerEvents: 'none',
                            zIndex: 0,
                        }
                    }}
                >
                    {/* ── HEADER ── */}
                    <Box sx={{
                        px: 2.5, py: 1.8,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        borderBottom: `1px solid ${T.border}`,
                        bgcolor: T.surface,
                        position: 'relative', zIndex: 1,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0, left: 24, right: 24,
                            height: '1px',
                            background: `linear-gradient(90deg, transparent, rgba(99,179,237,0.2), transparent)`,
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ position: 'relative' }}>
                                <BotAvatar size={36} />
                                <Box sx={{
                                    position: 'absolute', bottom: -2, right: -2,
                                    width: 9, height: 9, borderRadius: '50%',
                                    bgcolor: T.green,
                                    border: `2px solid ${T.surface}`,
                                }} />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <Typography sx={{
                                        color: T.text,
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        letterSpacing: '-0.01em',
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                        CRM Assistant
                                    </Typography>
                                    <Box sx={{
                                        px: 0.7, py: 0.1,
                                        bgcolor: 'rgba(99,179,237,0.1)',
                                        border: `1px solid rgba(99,179,237,0.2)`,
                                        borderRadius: '4px',
                                    }}>
                                        <Typography sx={{
                                            fontSize: '0.58rem',
                                            color: T.accent,
                                            fontFamily: "'JetBrains Mono', monospace",
                                            fontWeight: 500,
                                            letterSpacing: '0.04em',
                                        }}>AI</Typography>
                                    </Box>
                                </Box>
                                <Typography sx={{
                                    color: T.textMuted,
                                    fontSize: '0.68rem',
                                    fontFamily: "'DM Sans', sans-serif",
                                    mt: 0.1,
                                }}>
                                    {activeSociety?.name || 'All Societies'} · AutoRobos
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                                size="small"
                                onClick={handleClear}
                                sx={{
                                    color: T.textMuted,
                                    width: 30, height: 30,
                                    borderRadius: '8px',
                                    '&:hover': {
                                        color: '#fc8181',
                                        bgcolor: 'rgba(252,129,129,0.1)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setOpen(false)}
                                sx={{
                                    color: T.textMuted,
                                    width: 30, height: 30,
                                    borderRadius: '8px',
                                    '&:hover': {
                                        color: T.text,
                                        bgcolor: T.surfaceHigh,
                                    },
                                    transition: 'all 0.2s',
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* ── MESSAGES ── */}
                    <Box
                        className="scroll-area"
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: 2, py: 1.5,
                            display: 'flex', flexDirection: 'column', gap: 1.2,
                            position: 'relative', zIndex: 1,
                        }}
                    >
                        {/* Welcome state */}
                        {(chatState?.messages?.length || 0) === 0 && (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                                py: 3, gap: 2,
                            }}>
                                {/* Icon */}
                                <Box sx={{
                                    width: 52, height: 52,
                                    borderRadius: '16px',
                                    background: `linear-gradient(135deg, #1a3a5c 0%, #0d2137 100%)`,
                                    border: `1px solid ${T.borderHover}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: `0 0 24px ${T.accentGlow}`,
                                }}>
                                    <SparkleIcon sx={{ color: T.accent, fontSize: 26 }} />
                                </Box>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{
                                        color: T.text,
                                        fontSize: '0.88rem',
                                        fontWeight: 600,
                                        fontFamily: "'DM Sans', sans-serif",
                                        mb: 0.4,
                                    }}>
                                        How can I help you today?
                                    </Typography>
                                    <Typography sx={{
                                        color: T.textMuted,
                                        fontSize: '0.72rem',
                                        fontFamily: "'DM Sans', sans-serif",
                                        maxWidth: 260,
                                        lineHeight: 1.6,
                                    }}>
                                        Ask me about plots, leads, payments, or customers
                                    </Typography>
                                </Box>

                                {/* Quick query chips */}
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 0.8,
                                    width: '100%',
                                    maxWidth: 320,
                                }}>
                                    {visibleQueries.map((q, idx) => (
                                        <Box
                                            key={q.label}
                                            onClick={() => handleSend(q.query)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.8,
                                                px: 1.2, py: 1,
                                                bgcolor: T.surface,
                                                border: `1px solid ${T.border}`,
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: T.surfaceHigh,
                                                    borderColor: T.borderHover,
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 4px 16px ${T.accentGlow2}`,
                                                },
                                                animation: `fadeSlideUp 0.3s ${idx * 0.05}s both`,
                                            }}
                                        >
                                            <Typography sx={{
                                                fontSize: '0.9rem',
                                                lineHeight: 1,
                                                color: T.accent,
                                            }}>{q.icon}</Typography>
                                            <Typography sx={{
                                                fontSize: '0.7rem',
                                                color: T.textSub,
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontWeight: 500,
                                                lineHeight: 1.3,
                                            }}>{q.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Message bubbles */}
                        {(chatState?.messages || []).map((msg, idx) => (
                            <Box
                                key={idx}
                                className="chat-msg"
                                sx={{
                                    display: 'flex',
                                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                                    alignItems: 'flex-end',
                                    gap: 1,
                                    animationDelay: '0s',
                                }}
                            >
                                {msg.role === 'user' ? <UserAvatar /> : <BotAvatar />}

                                <Box sx={{
                                    maxWidth: '78%',
                                    display: 'flex', flexDirection: 'column',
                                    gap: 0.3,
                                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                }}>
                                    <Box sx={{
                                        background: msg.role === 'user' ? T.userBubble : T.botBubble,
                                        color: T.text,
                                        px: 1.6, py: 1.2,
                                        borderRadius: msg.role === 'user'
                                            ? '16px 16px 4px 16px'
                                            : '16px 16px 16px 4px',
                                        border: `1px solid ${msg.role === 'user' ? 'rgba(99,179,237,0.15)' : T.border}`,
                                        boxShadow: msg.role === 'user'
                                            ? '0 2px 12px rgba(30,90,150,0.3)'
                                            : '0 2px 8px rgba(0,0,0,0.3)',
                                    }}>
                                        {msg.role === 'assistant'
                                            ? <FormattedMessage content={msg.content} />
                                            : (
                                                <Typography sx={{
                                                    fontSize: '0.8rem',
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    lineHeight: 1.6,
                                                    color: T.text,
                                                }}>
                                                    {msg.content}
                                                </Typography>
                                            )
                                        }
                                    </Box>
                                    <Typography sx={{
                                        fontSize: '0.6rem',
                                        color: T.textMuted,
                                        fontFamily: "'JetBrains Mono', monospace",
                                        px: 0.5,
                                    }}>
                                        {msg.createdAt
                                            ? new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                            : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        {loading && <TypingIndicator />}

                        <div ref={messagesEndRef} />
                    </Box>

                    {/* ── INPUT ── */}
                    <Box sx={{
                        px: 2, py: 1.5,
                        borderTop: `1px solid ${T.border}`,
                        bgcolor: T.surface,
                        display: 'flex', gap: 1, alignItems: 'flex-end',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 24, right: 24,
                            height: '1px',
                            background: `linear-gradient(90deg, transparent, rgba(99,179,237,0.15), transparent)`,
                        }
                    }}>
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            multiline
                            maxRows={3}
                            size="small"
                            placeholder="Ask about plots, leads, payments…"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            disabled={loading}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: T.bg,
                                    color: T.text,
                                    fontSize: '0.8rem',
                                    fontFamily: "'DM Sans', sans-serif",
                                    borderRadius: '12px',
                                    transition: 'all 0.2s',
                                    '& fieldset': {
                                        borderColor: T.border,
                                        transition: 'all 0.2s',
                                    },
                                    '&:hover fieldset': { borderColor: 'rgba(99,179,237,0.25)' },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(99,179,237,0.4)',
                                        borderWidth: '1px',
                                        boxShadow: `0 0 0 3px ${T.accentGlow2}`,
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    py: 1,
                                    '&::placeholder': {
                                        color: T.textMuted,
                                        opacity: 1,
                                        fontFamily: "'DM Sans', sans-serif",
                                    },
                                },
                            }}
                        />

                        <Box
                            onClick={() => handleSend()}
                            sx={{
                                width: 38, height: 38,
                                borderRadius: '12px',
                                background: (!input.trim() || loading)
                                    ? T.surfaceHigh
                                    : `linear-gradient(135deg, #1a5a9c 0%, #0d3a6e 100%)`,
                                border: `1px solid ${(!input.trim() || loading) ? T.border : 'rgba(99,179,237,0.3)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: (!input.trim() || loading) ? 'not-allowed' : 'pointer',
                                flexShrink: 0,
                                transition: 'all 0.2s cubic-bezier(.22,1,.36,1)',
                                boxShadow: (!input.trim() || loading) ? 'none' : `0 4px 16px rgba(99,179,237,0.2)`,
                                '&:hover': (!input.trim() || loading) ? {} : {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 6px 20px rgba(99,179,237,0.3)`,
                                },
                                '&:active': { transform: 'scale(0.95)' },
                            }}
                        >
                            {loading
                                ? <CircularProgress size={14} sx={{ color: T.textMuted }} />
                                : <SendIcon sx={{
                                    fontSize: 16,
                                    color: (!input.trim()) ? T.textMuted : '#fff',
                                    transform: 'translateX(1px)',
                                }} />
                            }
                        </Box>
                    </Box>

                    {/* Footer watermark */}
                    <Box sx={{
                        textAlign: 'center', py: 0.6,
                        bgcolor: T.surface,
                        borderTop: `1px solid ${T.border}`,
                    }}>
                        <Typography sx={{
                            fontSize: '0.58rem',
                            color: T.textMuted,
                            fontFamily: "'JetBrains Mono', monospace",
                            letterSpacing: '0.06em',
                        }}>
                            POWERED BY AUTOROBOS AI
                        </Typography>
                    </Box>
                </Box>
            )}
        </>
    );
}